/**
 * Internal plan key → merchant-facing plan name.
 *
 * ⚠️ THE KEYS ARE DELIBERATELY OFFSET FROM THE NAMES. This is not a typo and
 * must not be "fixed":
 *
 *     key "starter"  →  sold as "Growth"  ($19)
 *     key "growth"   →  sold as "Scale"   ($49)
 *
 * Rendering the raw key would show a merchant paying for **Growth** a badge
 * reading "STARTER PLAN", and a merchant on **Scale** one reading "GROWTH" —
 * i.e. every paid tier mislabelled by one step, and one of them mislabelled as
 * a cheaper tier they are not on.
 *
 * The keys are also the Shopify App Pricing `plan_handle` values, which the
 * Partner Dashboard marks "Can't be changed later" — so this skew is permanent
 * and the mapping is load-bearing rather than transitional.
 *
 * SOURCE OF TRUTH: `api/helpers/planEntitlements.js` (`displayName`). That module
 * is backend-only — importing `api/**` into the browser bundle crosses the
 * boundary — so these strings are mirrored here on purpose. If a plan is ever
 * renamed in the Partner Dashboard, both files change together.
 */
/**
 * The Founding Merchant private plan's key/handle.
 *
 * Exported as a constant because it is stringly-typed in several places and the
 * value is IMMUTABLE — it was set in the Shopify Partner Dashboard, which marks
 * both the plan name and the handle "Can't be changed later". Note the HYPHEN:
 * `PLAN_ENTITLEMENTS["founding-merchant"]` must be bracket-accessed, and
 * `normalizeHandle` in api/helpers/planEntitlements.js resolves an unknown handle
 * by falling back to Free SILENTLY rather than throwing, so a typo here surfaces
 * as a Founder quietly holding free-tier limits.
 */
export const FOUNDING_MERCHANT_KEY = "founding-merchant";

export const PLAN_DISPLAY_NAMES = Object.freeze({
  free: "Free",
  starter: "Growth",
  growth: "Scale",
  pro: "Pro",
  // Founding Merchant — $199/YEAR private launch plan. Quoted because the
  // Partner Dashboard handle is hyphenated and immutable.
  //
  // ⛔ DISPLAY NAME ONLY — deliberately NOT in PLAN_CATALOGUE below, and adding
  // it there is not the missing half of this change. That array renders the
  // PUBLIC plan cards on /billing, and this plan is private: it is sold by
  // invitation, not picked off the pricing grid. BillingSkeleton.jsx:63 also
  // hardcodes `PLAN_CARD_COUNT = 4` to match the catalogue's length, so a fifth
  // entry makes the skeleton render one card short of the real page and the
  // layout jumps on load.
  //
  // This map has the opposite job: it labels a plan a shop is ALREADY on
  // (badges, the current-plan line, trial banners), so it must know every key
  // that can reach `shopifyShop.currentPlan` — including private ones. Without
  // it, planDisplayName() falls through to its key fallback and a Founder's
  // badge reads the raw "founding-merchant".
  "founding-merchant": "Founding Merchant",
});

/**
 * Merchant-facing name for a plan key.
 *
 * Falls back to the key itself rather than throwing or rendering blank — an
 * unrecognised key means a plan was added in the Partner Dashboard without
 * updating this map, and showing *something* beats showing nothing on a
 * billing screen.
 *
 * @param {string|null|undefined} planKey
 * @returns {string|null}
 */
export function planDisplayName(planKey) {
  if (!planKey) return null;
  return PLAN_DISPLAY_NAMES[planKey] ?? planKey;
}

/**
 * Master feature list — the superset across all plans, in display order.
 * A plan's card ticks the ones in its own `features` array and greys the rest.
 *
 * ⚠️ EXACT STRING MATCH — billing.jsx:840 does `plan.features.includes(featureName)`
 * against this list, so a string that differs by one character renders a ✗ on
 * EVERY plan, including Pro. That has already shipped once: the old billing page
 * hardcoded "All 13 campaign types" in its master list while testing membership
 * against stale `plan` DB rows that said 10, so the row was a cross everywhere.
 * Keeping both halves in this one file is what stops that from recurring.
 *
 * ⚠️ The app ships 13 campaign types, but they are no longer one row. Social proof
 * is PAID-ONLY (enforced in api/models/campaign/actions/{create,update}.js via
 * `canUseSocialProof`), so the old single "All 13 campaign types" row is split:
 *   "12 core campaign types"   — every plan, Free included
 *   "Social proof campaigns"   — starter / growth / pro only
 * 12 + 1 = 13. Editing only the Free entry would have been the shipped bug above.
 * The same split lives in `api/actions/seedPlans.js`; change both together.
 */
export const ALL_PLAN_FEATURES = Object.freeze([
  "AI image generation",
  "Smart Targeting (AI auto-targeting)",
  "AI variants + theme refinement + review",
  "12 core campaign types",
  "Social proof campaigns",
  "Email integrations",
  "Analytics dashboard",
  // Pro only (campaignLimit: null). The per-plan live-campaign counts render in
  // billing.jsx's LIMIT_ROWS "Live campaigns" row, not here.
  "Unlimited live campaigns",
  // Scale + Pro (decided 2026-09-23): their messages are answered before Free and
  // Growth. A queue ORDER, never a response time — no number may be attached to it.
  "Priority support",
]);

/**
 * Frontend plan catalogue — what the billing page renders.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A HARDCODED MIRROR AND NOT A QUERY
 * ─────────────────────────────────────────────────────────────────────────────
 * Two candidate sources were rejected:
 *
 *   `api/helpers/planEntitlements.js` — the real source of truth, but backend
 *   only. Importing `api/**` into the browser bundle crosses the boundary.
 *
 *   the `plan` DB model — what the OLD billing page queried, and what this
 *   replaces. That model is scheduled for deletion in Phase 4 of the App Pricing
 *   migration, so new UI must not take a dependency on it. Its rows are also
 *   already stale (see the campaign-type note above).
 *
 * So these numbers are mirrored by hand, exactly like PLAN_DISPLAY_NAMES above,
 * and change together with `PLAN_ENTITLEMENTS`. `null` means unlimited, matching
 * that module's convention.
 *
 * ⚠️ `price` here is DISPLAY ONLY. Shopify's hosted pricing page is what
 * actually charges, and its Partner Dashboard values are authoritative. If the
 * two ever disagree the merchant sees one number and pays another, which is an
 * App Store 4.2.1 accuracy problem — so treat a price edit here as requiring the
 * same edit in the Partner Dashboard, not as a copy change.
 *
 * ⚠️ KEEP IN SYNC — the per-plan quotas below are mirrored in FOUR places:
 *   1. this catalogue (billing page UI)
 *   2. `api/helpers/planEntitlements.js` (PLAN_ENTITLEMENTS — backend source of truth)
 *   3. `api/actions/seedPlans.js`        (seeds the `plan` DB model)
 *   4. `api/services/PlanLimits.js`      (LIMITS — enforcement)
 * `impressionLimit` (monthly popup impressions) is the current meter; `visitorLimit`
 * (monthly pageviews) is the legacy one and is retained, not replaced.
 *
 * ⛔ PUBLIC PLANS ONLY, and it is deliberately SHORTER than PLAN_DISPLAY_NAMES.
 * "founding-merchant" is a private, invitation-only plan and is intentionally
 * absent — see the note on that key above before adding a fifth card. This array
 * is also length-coupled: BillingSkeleton.jsx:63 hardcodes PLAN_CARD_COUNT = 4 to
 * match it, by hand, and a mismatch shifts the page on load.
 */
export const PLAN_CATALOGUE = Object.freeze([
  Object.freeze({
    key: "free",
    name: "Free",
    price: 0,
    description: "Get started for free",
    popular: false,
    visitorLimit: 500,
    impressionLimit: 500,
    campaignLimit: 1, // live campaigns
    aiOperationLimit: 1,
    aiCoPilotEditLimit: 20,
    features: Object.freeze(["12 core campaign types", "Analytics dashboard"]),
  }),
  // key "starter" is sold as "Growth" — the skew documented at the top of this file.
  Object.freeze({
    key: "starter",
    name: "Growth",
    price: 19,
    description: "Perfect for growing stores",
    popular: false,
    visitorLimit: 5000,
    impressionLimit: 5000,
    campaignLimit: 3, // live campaigns
    aiOperationLimit: 10,
    aiCoPilotEditLimit: 200,
    features: Object.freeze([
      "AI image generation",
      "Smart Targeting (AI auto-targeting)",
      "AI variants + theme refinement + review",
      "12 core campaign types",
      "Social proof campaigns",
      "Email integrations",
      "Analytics dashboard",
    ]),
  }),
  // key "growth" is sold as "Scale".
  Object.freeze({
    key: "growth",
    name: "Scale",
    price: 49,
    description: "For established businesses",
    // The one plan flagged `popular: true` in the plan table — preserved so the
    // "MOST POPULAR" pill still lands on the same tier it always has.
    popular: true,
    visitorLimit: 15000,
    impressionLimit: 15000,
    campaignLimit: 10, // live campaigns
    aiOperationLimit: 25,
    aiCoPilotEditLimit: 1000,
    features: Object.freeze([
      "AI image generation",
      "Smart Targeting (AI auto-targeting)",
      "AI variants + theme refinement + review",
      "12 core campaign types",
      "Social proof campaigns",
      "Email integrations",
      "Analytics dashboard",
      "Priority support",
    ]),
  }),
  Object.freeze({
    key: "pro",
    name: "Pro",
    price: 149,
    description: "For high-volume stores",
    popular: false,
    visitorLimit: 50000,
    impressionLimit: 50000,
    campaignLimit: null, // unlimited live campaigns
    aiOperationLimit: 75,
    aiCoPilotEditLimit: 3000,
    features: Object.freeze([
      "AI image generation",
      "Smart Targeting (AI auto-targeting)",
      "AI variants + theme refinement + review",
      "12 core campaign types",
      "Social proof campaigns",
      "Email integrations",
      "Analytics dashboard",
      "Unlimited live campaigns",
      "Priority support",
    ]),
  }),
]);

export default planDisplayName;
